import * as React from 'react';
import { Performance } from '../../classes/performance';
import { IOption, IOptionGroup } from '../../classes/seating-option';
import RadioGroup from '../../common/radio/radio-group';
import { bindKeypress, getFocusableChildren } from '../../helpers/window-helpers';
import { IAppSettingsModel } from '../../reducers/fetch-settings-reducer';

class PerformanceOption implements IOption {
    public id: string;
    public name: string;
    public selected: boolean = false;
    public performanceName?: string;
}

class PerformanceOptionGroup implements IOptionGroup {
    public id: string;
    public name: string;
    public isCheckbox: boolean;
    public seatLimit: number;
    public seatLimitText: string;
    public groupSeatSelectedQuantity: number;
    public groupSelected: boolean;
    public options: Array<PerformanceOption>;
    public selectedOptionId: string;
}

interface IPerformanceSelectorProps {
    appSettingsModel: IAppSettingsModel;
    productionPerformances: Array<Performance>;
    closeModalFunction?: () => void;
    title: string;
    isCancelable: boolean;
}

class PerformanceSelector extends React.Component<IPerformanceSelectorProps, { selectedPerformance: string, lastKeyDownFunction: any, lastFocusableElements: Array<any> }>{
    constructor(props) {
        super(props);
        this.state = {
            selectedPerformance: undefined,
            lastKeyDownFunction: null,
            lastFocusableElements: []
        };
    }

    componentDidMount() {
        var modal = document.getElementById('modal-container');
        if (modal != null) {
            let focusableElements = getFocusableChildren(modal);
            var focusFunction = event => bindKeypress.bind(this)(event, focusableElements, this.props.closeModalFunction.bind(this));
            if (this.state.lastKeyDownFunction != null) {
                window.removeEventListener('keydown', this.state.lastKeyDownFunction);
            }
            this.setState({ ...this.state, lastKeyDownFunction: focusFunction, lastFocusableElements: focusableElements });
            window.addEventListener('keydown', focusFunction);
        }
    }

    componentDidUpdate(nextProps, nextState) {
        var modal = document.getElementById('modal-container');
        if (modal != null) {
            if (this.state.selectedPerformance !== nextState.selectedPerformance) {
                let focusableElements = getFocusableChildren(modal);
                var focusFunction = event => bindKeypress.bind(this)(event, focusableElements, this.props.closeModalFunction.bind(this));

                window.removeEventListener('keydown', this.state.lastKeyDownFunction);

                this.setState({ ...this.state, lastKeyDownFunction: focusFunction, lastFocusableElements: focusableElements });
                window.addEventListener('keydown', focusFunction);
            }
        }
    }

    componentWillUnmount() {
        window.removeEventListener('keydown', bindKeypress.bind(this));
    }

    /**
     * When a new radio button is selected, update the state appropriately.
     * @param selectedRadio
     */
    _onNewRadioSelect = (selectedRadio) => {
        this.setState({
            selectedPerformance: selectedRadio[0].id
        });
    };

    /**
     * Redirect the user to the appropriate performance id page when they select a different performance.
     */
    _onOkClick() {
        const newQuery = `itemNumber=${this.state.selectedPerformance}`

        if (this.state.selectedPerformance !== undefined) {
            window.location.search = newQuery;
        }
    }

    hasSamePerformanceNames() {
        return this.props.appSettingsModel.productionPerformances.every(perf => {
            return perf.title === this.props.appSettingsModel.productionPerformances[0].title;
        });
    }

    getPerformanceOptions() {
        const showPerformanceTitles = this.props.appSettingsModel.showPerformanceTitleInDateModal && !this.hasSamePerformanceNames();

        let performanceOptionArray: Array<PerformanceOption> = this.props.appSettingsModel.productionPerformances.map(perf => {
            return {
                id: perf.id.toString(),
                name: `${showPerformanceTitles ? ` ${perf.title}<span class="normal-weight"> | ` : ''}${perf.dateString} | ${perf.timeString}${showPerformanceTitles ? '</span>' : ''}`,
            } as PerformanceOption;
        });

        // Don't show the current performance as an option.
        performanceOptionArray = performanceOptionArray.filter((performanceOption => {
            return performanceOption.id !== this.props.appSettingsModel.itemId.toString();
        }).bind(this));

        return performanceOptionArray;
    }

    render() {
        const useTagLogo = '<use xlink:href="#app-logo" />';
        const useTagBackicon = '<use xlink:href="#icon-arrow-left" />';

        const performanceOptions = this.getPerformanceOptions();

        const performanceOptionGroup: PerformanceOptionGroup = {
            id: "productionPerformances",
            name: "production",
            isCheckbox: false,
            options: performanceOptions,
            selectedOptionId: this.state.selectedPerformance
        } as PerformanceOptionGroup;

        let disableOkButton = (!this.props.isCancelable) ? false : this.state.selectedPerformance === undefined;

        return (
            // Modal container
            <div className="modal-wrapper">
                <div className="modal-overlay" tabIndex={-1} onClick={this.props.isCancelable ? this.props.closeModalFunction : null} />
                <section tabIndex={-1} role="dialog" aria-labelledby="modal-title" className="modal-container modal-container-performance-list" id="modal-container">
                    <div role="document">
                        <div className="modal-content">
                            <div className="heading-general">
                                {
                                    this.props.appSettingsModel.showPerformanceTitleInDateModal && this.hasSamePerformanceNames() ?
                                        <>
                                            <h3 id="modal-title">{this.props.productionPerformances[0].title}</h3>
                                            <p className="subheading">{this.props.title}</p>
                                        </>
                                        : <h3 id="modal-title">{this.props.title}</h3>
                                }
                            </div>
                            <div className="modal-performance-list">
                                {performanceOptionGroup.options.length > 0 ?
                                    <RadioGroup disabled={false} group={performanceOptionGroup} stateChangeFunction={this._onNewRadioSelect} />
                                    : <p>{this.props.appSettingsModel.allPerformancesPassedModalMessage}</p>}
                            </div>
                        </div>
                        <div className="modal-cta-container">
                            {this.props.isCancelable ? <button className="dialog-cta-btn" onClick={() => { this.props.closeModalFunction() }}>{this.props.appSettingsModel.changeDateModalCancelbutton}</button> : null}
                            <button className={"dialog-cta-btn" + (disableOkButton ? " disabled" : "")} disabled={(disableOkButton ? true : false)} onClick={() => {
                                performanceOptionGroup.options.length > 0 ?
                                    this._onOkClick()
                                    : this.props.closeModalFunction()
                            }}>{this.props.appSettingsModel.changeDateModalOKbutton}</button>
                        </div>
                    </div>
                </section>
            </div>
        );
    }
}

export default PerformanceSelector;